import { S } from './state.js';
import { Audio } from './audio.js';
import { applyBurn, applyChill, applyOnHitStatuses, combatHeal, damageEnemy, detonateDot, overload } from './combat.js';
import { BAL } from './config.js';
import { WEAPONS } from './data/weapons.js';
import { TYPES, rnd, rrange } from './fx.js';
import { MAX_PROC_DEPTH } from './meter.js';
import { player } from './entities.js';
import { clampToWorld, pushOutOfRocks } from './world.js';
// ---- Particles / juice ----
export function spawnParticle(x, y, type) {
  const c = TYPES[type] ? TYPES[type].glow : '#9fb4ff';
  S.particles.push({
    x, y, vx: rrange(-2.5, 2.5), vy: rrange(-3, 1),
    life: 1, color: c, r: rrange(1.5, 3.5),
  });
}
S.texts = [];
export function popText(a, val) { S.texts.push({ x: a.x, y: a.y, life: 1, val: val }); }

// ---- Proc / event system (the part that makes items chain into each other) ----
// Events: 'coin', 'hit', 'kill'. Items react to some events and may EMIT new ones
// (shocks collect coins → 'coin'; kills drop coins → 'coin'), so a single shot can
// avalanche through the whole inventory. Depth + per-frame budget keep it finite.
export function chance(p) { return rnd() < Math.min(0.95, p * S.procChanceMult); }   // Resonator boosts every proc
// shortest squared distance from point (px,py) to the segment (ax,ay)->(bx,by) — used for swept bullet hits
export function segDist2(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = ax + t * dx, cy = ay + t * dy;
  return (px - cx) * (px - cx) + (py - cy) * (py - cy);
}

export function fireEvent(type, x, y, depth) {
  if (depth >= MAX_PROC_DEPTH || S.procThisFrame > 240) return;
  S.procThisFrame++;
  if (type === 'coin') {
    if (S.items.static && chance(0.16 * S.items.static)) emitShock(x, y, 80 + S.items.static * 28, depth + 1, null, 'static');
    if (S.items.surge && chance(0.14 * S.items.surge)) emitSparkChain(x, y, 1 + S.items.surge, depth + 1);
    if (S.items.midas && chance(0.22 + 0.05 * S.items.midas)) {
      const extra = 1 + Math.floor(S.items.midas / 3);
      for (let i = 0; i < extra; i++) spawnBonusCoin(x, y);
    }
  } else if (type === 'hit') {
    // Volatile + Conductor: each bullet hit detonates / arcs, looping into 'coin' procs
    const ws = Math.min(1, WEAPONS[S.weapon].dmgMul || 1);   // on-hit blasts carry the gun's per-shot weight (anti fast-gun farm)
    if (S.items.explosive) emitShock(x, y, 70 + dimin(S.items.explosive) * 30, depth + 1, ws, 'volatile');   // radius growth diminishes past 3 stacks (anti-farm)
    if (S.items.conductor && chance(0.5)) emitSparkChain(x, y, 1 + S.items.conductor, depth + 1);
    // Tesla Coil: chain lightning that arcs through nearby ENEMIES for real damage
    if (S.items.tesla) chainLightning(x, y, S.DAMAGE * 0.6, 2 + S.items.tesla, depth + 1);
    if (S.EVO && S.EVO.arcs && chance(0.35)) chainLightning(x, y, S.DAMAGE * 0.45, 2, depth + 1, 'teslalance');   // Tesla Lance (evolved Beam): hits arc
    if (S.items.stormcore) chainLightning(x, y, S.DAMAGE * 0.5, 1 + S.items.stormcore, depth + 1, 'stormcore');   // Storm Core keystone: every hit arcs (+1 jump per extra copy — dupes did nothing)
    if (S.items.arcwell && chance(0.34)) chainLightning(x, y, S.DAMAGE * 0.4, 1 + S.items.arcwell, depth + 1, 'arcwell');   // Arc Well: accessible enemy-damage storm core (28%→34% in 0.14)
    if (S.items.pickpocket && chance(0.04 * S.items.pickpocket)) spawnBonusCoin(x, y);   // Pickpocket: Minter for everyone, weaker
    // 0.27 fun procs (on-hit) — novas & seekers that reuse the chain/shock helpers
    if (S.items.arclash && chance(0.25)) chainLightning(x, y, S.DAMAGE * 0.7, 3 + (S.items.arclash - 1), depth + 1, 'arc');   // Arc Lash: forked lightning
    if (S.items.frostbloom && chance(0.18)) { const r = 130 + S.items.frostbloom * 20; emitShock(x, y, r, depth + 1, 0.6, 'frostbloom'); for (const e of S.enemies) if (!e.dead && Math.hypot(e.x - x, e.y - y) < r * S.RADIUS_MUL) applyChill(e, 0.4, 90); }   // Frostbloom: chilling nova
    if (S.items.quakeshot && chance(0.12)) { const r = 110 + S.items.quakeshot * 25; emitShock(x, y, r, depth + 1, 0.6, 'quake'); for (const e of S.enemies) { const d = Math.hypot(e.x - x, e.y - y); if (!e.dead && d < r * S.RADIUS_MUL && e.kind !== 'boss') { e.x += (e.x - x) / (d || 1) * 22; e.y += (e.y - y) / (d || 1) * 22; } } }   // Quake Shot: knockback shockwave
    if (S.items.prism && chance(0.20)) { let cnt = 3 + (S.items.prism - 1); for (const o of S.enemies) { if (cnt <= 0) break; if (!o.dead && Math.hypot(o.x - x, o.y - y) < 260) { damageEnemy(o, S.DAMAGE * 0.5, depth + 1, 'prism'); cnt--; } } }   // Prism Shard: scattering shards
    if (S.items.seeker && chance(0.30)) chainLightning(x, y, S.DAMAGE * 0.9, 2 + S.items.seeker, depth + 1, 'seeker');   // Seeker Swarm: seeking bolts
    if (S.relics && S.relics.STORMLORD) chainLightning(x, y, S.DAMAGE * 0.55, 3, depth + 1, 'stormcore');   // Tempest Crown (relic): EVERY hit arcs
    // 0.27 fun procs batch 3 (on-hit)
    if (S.items.acidburst && chance(0.12) && S.groundFires.length < BAL.econ.groundFireMax) S.groundFires.push({ x, y, r: 70 * S.RADIUS_MUL, life: 120, kind: 'toxic' });   // Acid Burst
    if (S.items.sparkscatter && chance(0.20)) chainLightning(x, y, S.DAMAGE * 0.4, 2, depth + 1, 'arc');   // Spark Scatter
    if (S.items.flamewave && chance(0.15)) { const r = 120 + S.items.flamewave * 20; emitShock(x, y, r, depth + 1, 0.7, 'pyreburst'); for (const e of S.enemies) if (!e.dead && Math.hypot(e.x - x, e.y - y) < r * S.RADIUS_MUL) applyBurn(e, S.BURN_DPS, S.BURN_DUR); }   // Flame Wave
    if (S.items.shockpulse && chance(0.14)) emitShock(x, y, 90 + S.items.shockpulse * 15, depth + 1, 0.6, 'volatile');   // Shock Pulse
    if (S.items.icespray && chance(0.16)) { const r = 120 + S.items.icespray * 20; emitShock(x, y, r, depth + 1, 0.5, 'frostbloom'); for (const e of S.enemies) if (!e.dead && Math.hypot(e.x - x, e.y - y) < r * S.RADIUS_MUL) applyChill(e, 0.5, 100); }   // Ice Spray
  } else if (type === 'kill') {
    // Frag Rounds: direct-gun kills (depth 0) burst into fragments. The fragments themselves hit at
    // depth 1, so a frag kill can't re-frag — bounded by design, not by the proc budget.
    if (S.items.fragrounds && depth === 0) {
      const nf = 1 + S.items.fragrounds;
      for (let i = 0; i < nf; i++) {
        const a = rrange(0, 6.28);
        S.pbullets.push({ x, y, vx: Math.cos(a) * 7, vy: Math.sin(a) * 7, life: 26, maxlife: 26,
                        dmg: S.DAMAGE * 0.4, r: 4, pierce: 0, bounced: 0, aoe: 0, shockMul: 0.4, aoeSrc: 'bigshot', frag: true, srcTag: 'frag' });
      }
    }
    if (S.items.explosive || S.mods.detonator) {   // stacked Detonators: wider AND harder (+25% shock dmg per extra — radius alone was invisible)
      // standalone tax (0.17): a full-price no-investment shock on EVERY kill carried any build
      // (9.2% of ALL damage, the #1 gear pick — "it enables every build", Discord). With real blast
      // commitment (2+ blast items beyond the gear itself) it keeps full price; alone it fires at 70%.
      let dsc = null;
      if (S.mods.detonator) {
        const committed = (S.arch.blast || 0) - S.mods.detonator >= 2;
        dsc = (committed ? 1 : 0.7) + 0.25 * (S.mods.detonator - 1);
      }
      emitShock(x, y, 90 + dimin(S.items.explosive || 0) * 35 + (S.mods.detonator || 0) * 45, depth + 1, dsc, S.items.explosive ? 'volatile' : 'detonator');   // radius +60→+45 per copy (0.17)
    }
    if (S.items.blast) emitShock(x, y, 100 + dimin(S.items.blast) * 40, depth + 1, null, 'blastcap');   // per-stack radius diminishes past 3 (anti-farm) — power stays in blast DAMAGE
    if (S.items.primer) emitShock(x, y, 60 + dimin(S.items.primer) * 20, depth + 1, null, 'primer');    // Primer: small on-kill shock — early blast building block
    if (S.items.chainreaction) chainBlast(x, y, depth + 1);   // Chain Reaction keystone: a growing, %-max-HP cascade (not just a flat shock)
    // Singularity: a kill collapses into a gravity well — it vacuums every coin in (the economy engine),
    // drags nearby foes together so your AoE/chains land on a cluster, then implodes for damage that GROWS
    // with the wealth it just swallowed (greed → power). The legendary "god run" enabler, now with teeth.
    if (S.items.singularity) {
      let pulled = 0;
      for (const c of S.coins) {
        if (c.got) continue;
        if (Math.abs(c.x - player.x) < S.W && Math.abs(c.y - player.y) < S.H) { c.mag = true; c.spd = (c.spd || 5) + 6; pulled++; }
      }
      const rad = (190 + S.items.singularity * 35) * S.RADIUS_MUL;
      S.effects.push({ type: 'shock', x, y, r: 8, maxR: rad, life: 1 });
      const idmg = (S.DAMAGE * 0.5 + Math.min(80, pulled) * 2.5) * (1 + (S.arch.greed || 0) * 0.06) * 0.83;   // wealth swallowed → bigger implosion (×0.83 = ~17% nerf)
      for (const o of S.enemies) {
        if (o.dead || Math.hypot(o.x - x, o.y - y) >= rad) continue;
        if (o.kind !== 'boss') { o.x += (x - o.x) * 0.22; o.y += (y - o.y) * 0.22; }   // gravity clusters the swarm for your other AoE
        damageEnemy(o, idmg, depth + 1, 'singularity');
      }
    }
    // 0.27 fun procs (on-kill) — bursts, vortices & the void-rift climax
    if (S.items.necrobolt) { let nb = null, nd = 1e18; for (const o of S.enemies) { if (o.dead) continue; const dd = (o.x - x) ** 2 + (o.y - y) ** 2; if (dd < nd) { nd = dd; nb = o; } } if (nb) damageEnemy(nb, S.DAMAGE * (1.2 + (S.items.necrobolt - 1) * 0.5), depth + 1, 'specter'); }   // Necrotic Burst
    if (S.items.gravewell) { const r = 150 + S.items.gravewell * 20; emitShock(x, y, r, depth + 1, 0.6, 'gravewell'); for (const o of S.enemies) { const d = Math.hypot(o.x - x, o.y - y); if (!o.dead && d < r * S.RADIUS_MUL && o.kind !== 'boss') { o.x += (x - o.x) * 0.2; o.y += (y - o.y) * 0.2; } } }   // Gravewell
    if (S.items.coinbomb && chance(0.25)) { emitShock(x, y, 90 + S.items.coinbomb * 15, depth + 1, 0.7, 'goldburst'); dropCoins(x, y, 1 + S.items.coinbomb); }   // Coin Bomb
    if (S.items.voidrend && (S.voidN = (S.voidN || 0) + 1) >= 8) { S.voidN = 0; const r = 200 + S.items.voidrend * 30; emitShock(x, y, r, depth + 1, 1.2, 'voidrift'); S.shake = Math.max(S.shake, 12); }   // Void Rend: every 8th kill erupts
    // 0.27 fun procs batch 3 (on-kill)
    if (S.items.bountyburst && chance(0.25)) dropCoins(x, y, 2 + S.items.bountyburst);   // Bounty Burst
    if (S.items.chainreact2 && chance(0.30)) chainBlast(x, y, depth + 1);   // Detonation
    if (S.items.graveburst) emitShock(x, y, 130 + S.items.graveburst * 20, depth + 1, 0.7, 'specter');   // Grave Burst
    if (S.items.soulflame && S.groundFires.length < BAL.econ.groundFireMax) S.groundFires.push({ x, y, r: (50 + S.items.soulflame * 12) * S.RADIUS_MUL * S.GROUNDFIRE_MUL, life: 120, src: 'scorch' });   // Soulflame
    if (S.items.embermine && chance(0.20) && S.groundFires.length < BAL.econ.groundFireMax) S.groundFires.push({ x, y, r: (45 + S.items.embermine * 12) * S.RADIUS_MUL * S.GROUNDFIRE_MUL, life: 100, src: 'scorch' });   // Ember Mine
    if (S.items.frostgrave) { const r = 130 * S.RADIUS_MUL; for (const o of S.enemies) if (!o.dead && Math.hypot(o.x - x, o.y - y) < r) applyChill(o, 0.6, 130); }   // Frost Grave
  }
}

// chain lightning: arc from a point through the nearest enemies, damaging each
export function chainLightning(x, y, dmg, jumps, depth, src, falloff) {
  src = src || 'tesla'; falloff = falloff || 1;   // the Arc Coil weapon passes src='arc' + falloff<1 so its extra links taper; item procs keep full power
  if (depth > 0) { if (depth >= MAX_PROC_DEPTH || S.procThisFrame > 240) return; S.procThisFrame++; }   // proc-sourced chains share the per-frame budget + depth cap (the Leyden/echo cascade was the ONLY heavy proc that never accounted for itself — runaway guard)
  const ss = dimin(S.arch.storm || 0);   // storm compounds like blast (more jumps × more damage/jump) → diminishing returns
  let j2 = jumps + Math.floor(ss / BAL.syn.stormPer) + (S.items.capacitor || 0) + (S.items.ionturbine ? 1 : 0) + (S.items.overvolt ? 1 : 0) + (S.BONUS_JUMPS || 0);   // Stormcaller: more jumps per storm item (Capacitor adds raw jumps, Ion Turbine & Overvolt one more each; Arc talent adds BONUS_JUMPS)
  j2 = Math.min(j2, BAL.syn.stormJumpCap);   // softcap the multi-target spread: a chain hitting an unbounded pack was storm's late-game runaway (knob: syn.stormJumpCap). Ballast kills can still fork past this.
  const d2 = dmg * (1 + ss * BAL.syn.stormDmg) * S.LIGHTNING_MUL;                     // …and more damage per jump (Spark Plug / Ion Turbine scale every bolt)
  let sx = x, sy = y;
  const pts = [{ x: sx, y: sy }];
  const hit = [];
  let forked = 0;   // Ballast: a killing bolt extends the chain — at most stacks extra jumps per chain
  const reach = (300 + S.CHAIN_REACH_BONUS + Math.max(0, S.LASER_RANGE - 360) * 0.5) * (1 + 0.4 * (S.mods.homing || 0)) * (1 + (S.BULLET_SPD_MUL - 1) * 0.5);   // Homing Rounds make lightning seek farther between links; Conduit/Ozone add flat reach; +Range past the 360 base extends chain reach at half rate (0.19); SLIPSTREAM (0.19): bullet speed carries the arc farther at half rate
  for (let j = 0; j < j2; j++) {
    let best = null, bd = reach * reach;
    for (const e of S.enemies) {
      if (e.dead || hit.indexOf(e) !== -1) continue;
      const dd = (e.x - sx) * (e.x - sx) + (e.y - sy) * (e.y - sy);
      if (dd < bd) { bd = dd; best = e; }
    }
    if (!best) break;
    hit.push(best); pts.push({ x: best.x, y: best.y });
    sx = best.x; sy = best.y;
    const wasBurning = best.burnT > 0;
    let ld = d2 * Math.pow(falloff, j);   // later links taper for the arc weapon (falloff<1); item procs use falloff=1
    if (S.items.overcurrent && best.hp >= best.maxHp - 0.5) ld *= 1 + 0.35 * S.items.overcurrent;   // Overcurrent: the opener bolt hits unhurt foes harder
    if (S.items.overvolt && (best.slowT > 0 || best.freezeT > 0)) ld *= 1 + 0.2 * S.items.overvolt;  // Overvolt: charge runs hot through the cold (storm-frost bridge, 0.19)
    let arcCrit = false;
    if (src === 'arc' && depth === 0 && j === 0 && S.CRIT > 0 && rnd() < Math.min(0.95, S.CRIT)) {   // the Arc Coil's OPENER crits like a real shot — without this, arc was locked out of the whole crit economy (Scope/Headhunter/True Strike) that makes beam scale; later links stay crit-less so it can't run away
      ld *= S.CRITMULT; arcCrit = true; popText({ x: best.x, y: best.y - 14 }, 'CRIT');
    }
    if (!arcCrit && S.items.highvoltage && S.CRIT > 0) {   // High Voltage: the storm-crit bridge — extends crit to MORE links (deeper than the opener) & lands bigger bolt-crits (raw rnd — a crit roll, not a proc chance)
      const hv = S.items.highvoltage;
      if (rnd() < S.CRIT * Math.min(1.5, 0.6 + 0.25 * (hv - 1))) { ld *= S.CRITMULT * (1 + 0.2 * (hv - 1)); popText({ x: best.x, y: best.y - 14 }, 'CRIT'); }
    }
    damageEnemy(best, ld, depth, src);
    if (best.dead && S.items.ballast && forked < S.items.ballast) { forked++; j2++; }   // Ballast: the kill forks onward
    if (S.items.staticnet && !best.dead) applyChill(best, Math.min(0.6, 0.10 * S.items.staticnet), 60);   // Static Net: storm→frost bridge
    if (wasBurning && !best.dead) overload(best, depth);   // Overload: lightning + fire → electro-blast
    // the Arc Coil's FIRST link counts as a real hit — statuses, on-hit procs, leech — minus crits, and
    // only once per trigger pull (bullets proc per shot × multishot, so they still outscale arc late).
    // Without this, status items were silently DEAD on arc — which 65% of players pilot (v≤0.8 telemetry).
    // Item-proc lightning (Tesla/Storm Core/…, depth > 0) stays proc-free so chains can't cascade themselves.
    if (src === 'arc' && depth === 0 && j === 0) {
      applyOnHitStatuses(best);
      if (S.SLOW > 0) applyChill(best, S.SLOW, 60);
      fireEvent('hit', best.x, best.y, 1);
      if (S.mods.lifesteal || S.LIFESTEAL_HIT > 0) combatHeal(1.0 * (S.mods.lifesteal || 0) + S.LIFESTEAL_HIT);
    }
  }
  if (pts.length > 1) S.effects.push({ type: 'spark', pts, life: 1 });
  if (S.items.echo && chance(0.16 * S.items.echo)) chainLightning(x, y, dmg, jumps, depth + 1, src, falloff);
}

// collecting a coin is the hub event — almost everything routes back through here
export function collectCoin(c, depth) {
  if (c.got) return;
  c.got = true;
  // Gilded: coins picked up during an active chain are worth extra
  const gildBonus = (S.chainTimer > 0 ? (S.items.gilded || 0) : 0);
  const cval = Math.round((Math.max(1, Math.round(S.GREED * S.fvGreed)) + gildBonus) * (c.worth || 1));   // worth = denomination (consolidated coins from dropCoins are worth more; identical income, fewer entities)
  S.coinsRun += cval; S.coinsEarned += cval;
  if (S.talentFlags.midas && depth < 1 && S.chainCount % 4 === 0) emitShock(c.x, c.y, 80, depth + 1, 0.45, 'midas');   // Midas Touch talent: collected coins burst for damage (every 4th, depth-guarded vs the coin web)
  if (S.mods.siphon) combatHeal(0.4 * S.mods.siphon);   // Siphon Core: coins heal — capped (coin-vacuum builds can't burst-refill)
  if (S.items.tithe && ++S.titheN >= 10) { S.titheN = 0; combatHeal(1 * S.items.tithe); }   // Tithe: every 10th coin tips the healer (same leech cap)
  S.chainCount++; S.chainTimer = 48;
  if (S._sfxCoin > 0) { S._sfxCoin--; Audio.play('coin', Math.min(20, S.chainCount)); }
  if (S.chainCount % 5 === 0) Audio.play('chain', Math.min(30, S.chainCount));
  if (S.chainCount > 0 && S.chainCount % 20 === 0) {   // big-cascade climax: flash + shake
    S.flash = Math.max(S.flash, 0.45);
    S.shake = Math.max(S.shake, 12); Audio.play('cascade');
  }
  for (let k = 0; k < 5; k++) spawnParticle(c.x, c.y, 'gold');
  // Payday (ascended Minter): every 8th coin collected fires a golden homing bullet at the nearest foe
  if (S.EVO2 && S.EVO2.payday && ++S.paydayCount >= 8) {
    S.paydayCount = 0;
    let pbest = null, pbd = 700 * 700;
    for (const e of S.enemies) {
      if (e.dead) continue;
      const dd = (e.x - player.x) ** 2 + (e.y - player.y) ** 2;
      if (dd < pbd) { pbd = dd; pbest = e; }
    }
    if (pbest) {
      const a = Math.atan2(pbest.y - player.y, pbest.x - player.x);
      S.pbullets.push({ x: player.x, y: player.y, vx: Math.cos(a) * 11, vy: Math.sin(a) * 11, life: 80, maxlife: 80,
                      dmg: S.DAMAGE * 0.85 * (0.8 + S.GREED * 0.4), r: 6, pierce: 1, bounced: 0, aoe: 0, shockMul: 0.85, aoeSrc: 'bigshot', homing: true, golden: true });
    }
  }
  // Catalyst: every milestone in a chain detonates a screen-shaking proc-bomb
  if (S.items.catalyst && S.chainCount % 8 === 0) {
    emitShock(player.x, player.y, 200 + S.items.catalyst * 70, depth + 1, null, 'catalyst');
    S.shake = Math.max(S.shake, 6);
  }
  if (S.items.mintstorm && S.chainCount % 12 === 0) {   // Mint Storm (0.22 Coinstorm path): every 12th chained coin erupts in a %-max-HP coin-vortex
    const mr = 180 * S.RADIUS_MUL;
    S.effects.push({ type: 'shock', x: player.x, y: player.y, r: 8, maxR: mr, life: 1 });
    for (const e of S.enemies) if (!e.dead && Math.hypot(e.x - player.x, e.y - player.y) < mr) damageEnemy(e, e.maxHp * BAL.syn.coinstormBite * Math.min(BAL.syn.pctBiteCap, S.items.mintstorm) * (e.kind === 'boss' ? 0.25 : 1) * S.biteScale, depth + 1, 'coinstorm');
  }
  fireEvent('coin', c.x, c.y, depth);
}

// chain lightning: arc from (x,y) through the nearest coins, collecting each
export function emitSparkChain(x, y, jumps, depth) {
  let sx = x, sy = y;
  const pts = [{ x: sx, y: sy }];
  const hit = [];
  for (let j = 0; j < jumps; j++) {
    let best = null, bd = 280 * 280;
    for (const c of S.coins) {
      if (c.got || hit.indexOf(c) !== -1) continue;
      const dd = (c.x - sx) * (c.x - sx) + (c.y - sy) * (c.y - sy);
      if (dd < bd) { bd = dd; best = c; }
    }
    if (!best) break;
    hit.push(best); pts.push({ x: best.x, y: best.y });
    sx = best.x; sy = best.y;
    collectCoin(best, depth);
  }
  if (pts.length > 1) S.effects.push({ type: 'spark', pts, life: 1 });
  if (S.items.echo && chance(0.16 * S.items.echo)) emitSparkChain(x, y, jumps, depth + 1);
}

// shock pulse: ring that collects every coin AND damages every enemy in radius.
// This is what fuses combat and the proc web — a shock from a coin pickup hurts
// enemies, and a shock from a kill grabs coins. Everything feeds everything.
// Some archetypes scale on TWO+ axes at once (blast: damage × area × cascade; storm: jumps × damage-per-jump),
// so raw stacking snowballs past every other source. Shared diminishing-returns curve for those synergy tallies:
// the first 3 stacks count fully, each one past that is worth half. Keeps early investment feeling good while
// stopping deep stacks from running away.
export function dimin(n) { return n <= 3 ? n : 3 + (n - 3) * 0.5; }
export function emitShock(x, y, radius, depth, dmgScale, src) {
  src = src || 'blast';   // damage-meter identity: every caller passes WHICH item/weapon made this shock
  if (depth > 0) { if (depth >= MAX_PROC_DEPTH || S.procThisFrame > 240) return; S.procThisFrame++; }   // proc-sourced shocks share the per-frame budget + depth cap (every emitShock is depth≥1; only the primary weapon fire is depth 0, and it doesn't route here) — runaway guard for the Leyden blast↔chain loop
  const bs = dimin(S.arch.blast || 0);
  // radius scales gently and is HARD-CAPPED so deep blast stacks can't turn every kill into a screen-wide farm;
  // the build's power lives in damage (below), not an ever-growing kill-zone
  const r2 = Math.min(440, radius * S.RADIUS_MUL * S.BLAST_R_MUL * (1 + bs * BAL.syn.blastRadius));   // Tamper widens inside the same hard cap
  // dmgScale: HIT-triggered shocks pass the gun's per-shot weight (≤1) so fast/weak guns (Repeater & co)
  // can't fire full-price explosions 20×/sec; kill- & coin-shocks pass nothing (full power).
  // Base 0.6→0.5: blast+chain was 53% of ALL damage dealt in the ≤0.6 telemetry batch.
  const edmg = S.DAMAGE * 0.5 * (dmgScale || 1) * (1 + bs * BAL.syn.blastDmg) * S.SHOCK_MUL;   // Powder Keg scales every shock
  S.effects.push({ type: 'shock', x, y, r: 8, maxR: r2, life: 1 });
  const len = S.coins.length;   // capture length so coins spawned this pulse wait their turn
  for (let i = 0; i < len; i++) {
    const c = S.coins[i];
    if (!c.got && Math.hypot(c.x - x, c.y - y) < r2) collectCoin(c, depth);
  }
  const elen = S.enemies.length;
  let scBest = null, scBd = Infinity;   // Shaped Charge: the foe nearest the blast heart eats a focused hit
  if (S.items.shapedcharge) for (let i = 0; i < elen; i++) {
    const e = S.enemies[i];
    if (e.dead) continue;
    const dd = Math.hypot(e.x - x, e.y - y);
    if (dd < r2 && dd < scBd) { scBd = dd; scBest = e; }
  }
  for (let i = 0; i < elen; i++) {
    const e = S.enemies[i];
    if (e.dead) continue;
    const d3 = Math.hypot(e.x - x, e.y - y);
    if (d3 < r2) {
      const afflicted = e.burnT > 0 || e.poison > 0;
      // distance falloff (0.14 redesign): full damage at the heart, 40% at the rim — one kill no
      // longer auto-clears its whole radius, so blast cascades need DENSITY instead of mere proximity
      // (Slag raises the rim floor toward 70%; Shaped Charge focuses the heart)
      damageEnemy(e, edmg * (1 - (0.6 - S.BLAST_RIM_BONUS) * (d3 / r2)) * (e === scBest ? 1 + 0.25 * S.items.shapedcharge : 1)
                   + (S.items.megaton ? e.maxHp * BAL.syn.megatonBite * Math.min(BAL.syn.pctBiteCap, S.items.megaton) * (e.kind === 'boss' ? 0.25 : 1) * S.biteScale : 0), depth, src);   // Megaton Mix: shocks bite % max HP — knob-tunable + stack-capped (0.21)
      if (S.items.thermobaric && !e.dead && chance(Math.min(0.9, 0.3 * S.items.thermobaric))) applyBurn(e, Math.max(12, S.BURN_DPS), 90);   // Thermobaric: the blast-fire bridge (dps floor so it works with zero fire items)
      if (S.items.overpressure && !e.dead && e.kind !== 'boss' && e.affix !== 'juggernaut' && d3 > 0.5) {   // Overpressure: the pressure wave shoves the pack
        e.x += (e.x - x) / d3 * 8 * S.items.overpressure; e.y += (e.y - y) / d3 * 8 * S.items.overpressure;
      }
      if (afflicted && !e.dead) detonateDot(e, depth);     // Detonate: blast consumes DoT for a burst
    }
  }
  if (S.items.leyden && chance(0.18 * S.items.leyden)) chainLightning(x, y, S.DAMAGE * 0.5, 1, depth + 1, 'leyden');   // Leyden Jar: the blast-storm bridge
  if (S.items.shrapnel) {   // Shrapnel: shards leap PAST the rim — blast pressure beyond its own radius
    let ns = 2 + S.items.shrapnel;
    for (let i = 0; i < elen && ns > 0; i++) {
      const e = S.enemies[i];
      if (e.dead) continue;
      const d3 = Math.hypot(e.x - x, e.y - y);
      if (d3 >= r2 && d3 < r2 + 170) { S.lasers.push({ x1: x, y1: y, x2: e.x, y2: e.y, life: 1 }); damageEnemy(e, edmg * 0.35, depth, src); ns--; }
    }
  }
  if (S.talentFlags.cluster && depth < 1) {   // Cluster talent: each blast throws 2 smaller daughter blasts (depth-guarded so they don't re-cluster)
    const ox = r2 * 0.6;
    emitShock(x - ox, y, radius * 0.5, depth + 2, (dmgScale || 1) * 0.5, 'cluster');
    emitShock(x + ox, y, radius * 0.5, depth + 2, (dmgScale || 1) * 0.5, 'cluster');
  }
  if ((S.items.submunitions || S.items.clusterrounds) && depth < 3 && S.bomblets.length < 40) {   // Saturation (0.22 blast NEW PATH): blasts litter the ground with delayed daughter bomblets (detonate in updateCombat)
    const nb = (S.items.submunitions ? 1 : 0) + (S.items.clusterrounds ? 1 + S.items.clusterrounds : 0);
    for (let i = 0; i < nb && S.bomblets.length < 40; i++) {
      const ba = rrange(0, 6.28), brad = rrange(20, Math.max(24, r2 * 0.8));
      S.bomblets.push({ x: x + Math.cos(ba) * brad, y: y + Math.sin(ba) * brad, t: 30, r: (60 + (S.items.clusterrounds || 0) * 10) * S.RADIUS_MUL * S.BLAST_R_MUL, dmg: 0.4 });
    }
  }
  if (S.items.echo && chance(0.16 * S.items.echo)) emitShock(x, y, radius * 0.85, depth + 1, dmgScale, src);
}

// Chain Reaction keystone: a kill sets off a detonation that GROWS with every link of the cascade and
// deals a %-of-max-HP bite — so the chain stays lethal at any wave and one kill in a pack can rip across
// the screen. (Blast Cap stays the flat, cheap building block; this is the build-defining capstone.)
export function chainBlast(x, y, depth) {
  if (S.procThisFrame > 240) return;                                   // share the per-frame proc budget (lag guard)
  const stacks = S.items.chainreaction || 1, link = Math.min(7, depth);   // deeper in the cascade ⇒ bigger blast (bounded)
  const bs = dimin(S.arch.blast || 0);                                 // diminishing-returns blast bonus (see dimin)
  const r = Math.min(560, (140 + stacks * 30) * S.RADIUS_MUL * (1 + link * 0.18)) * (1 + bs * BAL.syn.blastRadius);
  S.effects.push({ type: 'shock', x, y, r: 8, maxR: r, life: 1 });
  if (link <= 2) S.shake = Math.max(S.shake, 4 + link * 2);
  const clen = S.coins.length;   // still feeds the proc web — grabs coins like any blast
  for (let i = 0; i < clen; i++) { const c = S.coins[i]; if (!c.got && Math.hypot(c.x - x, c.y - y) < r) collectCoin(c, depth); }
  const elen = S.enemies.length;
  for (let i = 0; i < elen; i++) {
    const e = S.enemies[i];
    if (e.dead) continue;
    const d4 = Math.hypot(e.x - x, e.y - y);
    if (d4 >= r) continue;
    const afflicted = e.burnT > 0 || e.poison > 0;
    // flat gun damage + a %-max-HP bite (reduced vs bosses), both escalating with the chain link;
    // same distance falloff as emitShock (full → 40% at the rim)
    const edmg = (S.DAMAGE * 0.7 + e.maxHp * (e.kind === 'boss' ? 0.012 : BAL.syn.chainBite) * Math.min(BAL.syn.pctBiteCap, stacks) * S.biteScale)
               * (1 + bs * BAL.syn.blastDmg) * (1 + link * 0.12) * (1 - 0.6 * (d4 / r));
    damageEnemy(e, edmg, depth, 'chain');                            // a kill here re-enters fireEvent('kill') → chainBlast(depth+1): the cascade
    if (afflicted && !e.dead) detonateDot(e, depth);                 // still consumes DoT for the blast×elemental reaction
  }
  if (S.items.echo && depth < 9 && chance(0.16 * S.items.echo)) chainBlast(x, y, depth + 1);
}

// Cluster Doctrine (ascended Grenadier): a detonating shell splits into 3 bomblets (bomblets don't re-split)
export function clusterSplit(b) {
  if (!b.cluster) return;
  for (let i = 0; i < 3; i++) {
    const a = rrange(0, 6.28), sp = rrange(3.5, 5.5);
    S.pbullets.push({ x: b.x, y: b.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: rrange(10, 18), maxlife: 18,
                    dmg: b.dmg * 0.45, r: 5, pierce: 0, bounced: 0, aoe: b.aoe * 0.55, shockMul: b.shockMul, aoeSrc: b.aoeSrc, explodes: true });
  }
}
// a lobbed shell detonates: the blast + Cluster Doctrine bomblets + BOUNCE (the blast "skips" to the nearest
// foe(s), so ricochet finally does something for Cannon/Grenadier — see fireWeapon's delivery-stat redirects)
export function lobExplode(b) {
  if (b.aoe > 0) emitShock(b.x, b.y, b.aoe, 1, b.shockMul, b.aoeSrc);
  clusterSplit(b);
  if (b.lobBounce > 0 && b.aoe > 0) {
    let n = b.lobBounce, fx = b.x, fy = b.y; const seen = [];
    while (n-- > 0) {
      let best = null, bd = 360 * 360;
      for (const e of S.enemies) { if (e.dead || seen.indexOf(e) !== -1) continue; const dd = (e.x - fx) ** 2 + (e.y - fy) ** 2; if (dd < bd) { bd = dd; best = e; } }
      if (!best) break;
      seen.push(best); emitShock(best.x, best.y, b.aoe * 0.7, 1, (b.shockMul || 1) * 0.7, b.aoeSrc); fx = best.x; fy = best.y;
    }
  }
}
// a speed kick along your current heading; itself an event Surge can feed off
export function spawnBonusCoin(x, y, worth) {
  // pre-magnetized so it rushes in and gets collected, feeding the chain
  const cp = { x: x + rrange(-26, 26), y: y + rrange(-26, 26) };
  clampToWorld(cp, 16); pushOutOfRocks(cp, 10);   // wall kills can't fling coins past the edge or into a rock
  S.coins.push({ x: cp.x, y: cp.y, got: false, ph: rrange(0, 6.28), mag: true, spd: 7, worth: worth || 1 });
}
// Drop `count` coins' WORTH of money as a CAPPED number of entities. Deep-wave kills/payouts want to
// drop 100s of coins (count scales with wave) — that carpets the arena and tanks FPS. Past econ.coinMax
// we fold the surplus into each coin's `worth` (a denomination), so a kill makes few-but-fatter coins:
// identical income & coin-web value, a fraction of the entities. collectCoin multiplies value by worth.
export function dropCoins(x, y, count) {
  if (count <= 0) return;
  const cap = BAL.econ.coinMax || count;
  const n = Math.min(count, cap);
  const worth = count / n;
  for (let i = 0; i < n; i++) spawnBonusCoin(x, y, worth);
}
